import type {
  AnalyticsInterval,
  AnalyticsMetricName,
  AnalyticsSplitName,
} from "@lafayette/schemas";
import { useId, useState } from "react";
import { Link } from "react-router";

import { Button } from "../../ui/button";
import { Checkbox } from "../../ui/checkbox";
import { cn } from "../../ui/cn";
import { Icon } from "../../ui/icon";
import { Input } from "../../ui/input";
import { Select } from "../../ui/select";
import { METRIC_INFO, METRIC_NAMES, SPLIT_LABELS } from "./metric-catalog";
import {
  intervalAllowed,
  spanPhrase,
  startOfZonedDay,
  type Zoom,
  zonedDay,
  zoneOffsetMs,
  zoomChipText,
  zoomSpoken,
} from "./zoom-math";

// The controls of the Explore tab: back to the Overview, "Show" (the metric), "Split by", the interval (hour, day, week), the chip of the selection
// with its ×, the comparison with the previous period, and the date controls that replace the drag on a touch screen.

const INTERVALS: readonly Readonly<{ value: AnalyticsInterval; label: string }>[] = [
  { value: "hour", label: "Hour" },
  { value: "day", label: "Day" },
  { value: "week", label: "Week" },
];

const fieldLabel = "grid gap-1 text-[13px] font-medium text-muted-foreground";

function IntervalControl({
  value,
  bound,
  onChange,
}: Readonly<{
  value: AnalyticsInterval;
  bound: Zoom;
  onChange: (interval: AnalyticsInterval) => void;
}>) {
  return (
    // biome-ignore lint/a11y/useSemanticElements: a group of toggle buttons, as the range control
    <div
      role="group"
      aria-label="Interval"
      className="inline-flex gap-0.5 rounded-xl border border-hairline bg-muted p-[3px]"
    >
      {INTERVALS.map((option) => {
        const allowed = intervalAllowed(option.value, bound.from, bound.to);
        const pressed = option.value === value;
        return (
          <Button
            key={option.value}
            type="button"
            variant="ghost"
            aria-pressed={pressed}
            disabled={!allowed}
            title={allowed ? undefined : "This window is too long for this interval"}
            onClick={() => onChange(option.value)}
            className={cn(
              "rounded-[9px] px-3 text-[13px] leading-[18px] font-medium compact:h-8",
              pressed
                ? "bg-card font-semibold text-foreground hover:bg-card"
                : "text-muted-foreground hover:bg-transparent hover:text-foreground",
            )}
          >
            {option.label}
          </Button>
        );
      })}
    </div>
  );
}

function DateControls({
  tz,
  bound,
  onApply,
}: Readonly<{ tz: string; bound: Zoom; onApply: (zoom: Zoom) => void }>) {
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const fromId = useId();
  const toId = useId();
  const start = startOfZonedDay(from, tz);
  const lastDay = startOfZonedDay(to, tz);
  // The end is the start of the day AFTER the last day chosen, so both days are whole.
  const end =
    lastDay === null
      ? null
      : new Date(Date.parse(lastDay) + 24 * 3_600_000 + zoneShift(lastDay, tz)).toISOString();
  const first =
    start === null
      ? null
      : new Date(Math.max(Date.parse(start), Date.parse(bound.from))).toISOString();
  const last =
    end === null ? null : new Date(Math.min(Date.parse(end), Date.parse(bound.to))).toISOString();
  const valid =
    first !== null && last !== null && Date.parse(last) - Date.parse(first) >= 3_600_000;
  return (
    <details className="text-sm">
      <summary className="inline-flex min-h-11 cursor-pointer items-center gap-2 font-medium text-link compact:min-h-8">
        <Icon name="calendar-03" size="sm" />
        Choose dates
      </summary>
      <form
        className="mt-2 flex flex-wrap items-end gap-3"
        onSubmit={(event) => {
          event.preventDefault();
          if (valid) onApply({ from: first, to: last });
        }}
      >
        <div className={fieldLabel}>
          <label htmlFor={fromId}>From</label>
          <Input
            id={fromId}
            type="date"
            value={from}
            min={zonedDay(bound.from, tz)}
            max={zonedDay(bound.to, tz)}
            onChange={(event) => setFrom(event.target.value)}
          />
        </div>
        <div className={fieldLabel}>
          <label htmlFor={toId}>To</label>
          <Input
            id={toId}
            type="date"
            value={to}
            min={zonedDay(bound.from, tz)}
            max={zonedDay(bound.to, tz)}
            onChange={(event) => setTo(event.target.value)}
          />
        </div>
        <Button type="submit" variant="outline" disabled={!valid}>
          Zoom to these dates
        </Button>
      </form>
    </details>
  );
}

/** The change of offset between a day start and the next (a day with a clock change is not 24 hours long). */
function zoneShift(dayStart: string, tz: string): number {
  const at = Date.parse(dayStart);
  return zoneOffsetMs(at, tz) - zoneOffsetMs(at + 24 * 3_600_000, tz);
}

function ZoomChip({
  zoom,
  tz,
  onClear,
}: Readonly<{ zoom: Zoom; tz: string; onClear: () => void }>) {
  return (
    <span className="inline-flex min-h-11 items-center gap-1 compact:gap-2 rounded-xl bg-accent pr-1 pl-3 text-sm font-medium text-accent-foreground compact:min-h-9">
      <Icon name="calendar-03" size="sm" />
      <span aria-hidden="true">{zoomChipText(zoom, tz)}</span>
      <span className="sr-only">Selection: {zoomSpoken(zoom, tz)}</span>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        aria-label="Clear the selection"
        onClick={onClear}
        className="size-9 compact:size-7"
      >
        <Icon name="cancel-01" size="sm" />
      </Button>
    </span>
  );
}

function CompareSwitch({
  compare,
  bound,
  onCompare,
}: Readonly<{ compare: boolean; bound: Zoom; onCompare: (compare: boolean) => void }>) {
  const id = useId();
  return (
    <div className="inline-flex min-h-11 w-full sm:ml-auto sm:w-auto items-center gap-2.5 rounded-xl border border-input bg-card px-3 text-sm font-medium compact:min-h-9">
      <Checkbox
        id={id}
        role="switch"
        checked={compare}
        onChange={(event) => onCompare(event.target.checked)}
      />
      <label htmlFor={id} className="cursor-pointer">
        Compare with previous {spanPhrase(bound)}
      </label>
    </div>
  );
}

/** The metrics of the "Show" list. A customer who opted out gets the app metrics greyed, with the reason in the word. */
function MetricOptions({ optedOut }: Readonly<{ optedOut: boolean }>) {
  return (
    <>
      {METRIC_NAMES.map((name) => {
        const off = optedOut && METRIC_INFO[name].app;
        return (
          <option key={name} value={name} disabled={off}>
            {METRIC_INFO[name].label}
            {off ? " (not shared)" : ""}
          </option>
        );
      })}
    </>
  );
}

export function ExploreToolbar({
  overviewHref,
  metric,
  split,
  interval,
  compare,
  zoom,
  bound,
  whole,
  tz,
  optedOut,
  onMetric,
  onSplit,
  onInterval,
  onCompare,
  onClear,
  onDates,
}: Readonly<{
  overviewHref: string;
  metric: AnalyticsMetricName;
  split: AnalyticsSplitName;
  interval: AnalyticsInterval;
  compare: boolean;
  zoom: Zoom | null;
  /** The window that the chart shows now (the zoom, or the whole range). */
  bound: Zoom;
  /** The whole range of the page: the dates a person may choose. */
  whole: Zoom;
  tz: string;
  /** The customer chose not to share app analytics: the app metrics cannot be read. */
  optedOut: boolean;
  onMetric: (metric: AnalyticsMetricName) => void;
  onSplit: (split: AnalyticsSplitName) => void;
  onInterval: (interval: AnalyticsInterval) => void;
  onCompare: (compare: boolean) => void;
  onClear: () => void;
  onDates: (zoom: Zoom) => void;
}>) {
  const info = METRIC_INFO[metric];
  const showId = useId();
  const splitId = useId();
  return (
    <div className="grid gap-3">
      <div className="flex flex-wrap items-end gap-x-3 gap-y-3">
        <Link
          to={overviewHref}
          className="inline-flex min-h-11 items-center gap-1.5 rounded-xl bg-muted px-3 text-sm font-medium text-foreground no-underline hover:bg-border compact:min-h-9"
        >
          <Icon name="arrow-left-01-round" size="sm" />
          Overview
        </Link>
        <div className={fieldLabel}>
          <label htmlFor={showId}>Show</label>
          <Select
            id={showId}
            value={metric}
            onChange={(event) => onMetric(event.target.value as AnalyticsMetricName)}
            className="min-w-44"
          >
            <MetricOptions optedOut={optedOut} />
          </Select>
        </div>
        <div className={fieldLabel}>
          <label htmlFor={splitId}>Split by</label>
          <Select
            id={splitId}
            value={split}
            onChange={(event) => onSplit(event.target.value as AnalyticsSplitName)}
            className="min-w-36"
          >
            {info.splits.map((name) => (
              <option key={name} value={name}>
                {SPLIT_LABELS[name]}
              </option>
            ))}
          </Select>
        </div>
        <IntervalControl value={interval} bound={bound} onChange={onInterval} />
        {zoom === null ? null : <ZoomChip zoom={zoom} tz={tz} onClear={onClear} />}
        <CompareSwitch compare={compare} bound={bound} onCompare={onCompare} />
      </div>
      <DateControls tz={tz} bound={whole} onApply={onDates} />
    </div>
  );
}
